import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';

const ProductPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productResponses, setProductResponses] = useState([]); // To store responses for each product
  const [error, setError] = useState(null); // For handling errors
  const [loadingQuantities, setLoadingQuantities] = useState(true); // To track when city quantities are loaded

  const cities = [
    'Seattle, WA', 'Cincinnati, OH', 'Dallas, TX', 'Reno, NV', 
    'Robbinsville, NJ', 'Jacksonville, FL', 'Minneapolis, MN', 
    'Phoenix, AZ', 'Richmond, VA'
  ];

  useEffect(() => {
    // Fetch the CSV file from the public folder
    fetch('../manager.csv')  // Correct path to the file in the public folder
      .then((response) => response.text())  // Get the file as text
      .then((csvData) => {
        // Parse the CSV data using PapaParse
        Papa.parse(csvData, {
          header: true,
          skipEmptyLines: true,
          complete: (result) => {
            setProducts(result.data);  // Set the parsed data to the state
            setLoading(false);  // Stop loading when data is ready

            // Automatically call handleSubmit for each product in the CSV file
            result.data.forEach((product) => {
              handleSubmit(product);  // Pass the entire product to handleSubmit
            });
          },
          error: (error) => {
            console.error('Error parsing CSV:', error);
            setError('Error parsing CSV data.');
            setLoading(false);
          },
        });
      })
      .catch((error) => {
        console.error('Error fetching CSV file:', error);
        setError('Error fetching CSV file.');
        setLoading(false);
      });
  }, []);

  const handleSubmit = async (product) => {
    // Initial fields
    let { style, color, size } = product;
  
    // Convert size to lowercase (and keep it consistent for all attempts)
    size = size ? size.toLowerCase() : '';
    
    // Convert style and color to lowercase for the first attempt
    style = style ? style.toLowerCase() : '';
    color = color ? color.toLowerCase() : '';
  
    // Log the data being sent for the first attempt
    console.log('Sending data for product (initial attempt):', {
      style, 
      color, 
      size
    });
  
    try {
      // First API call with lowercase style, color, and size
      let response = await fetch('https://api-shipping-28612190fdf4.herokuapp.com/inventory', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ style, color, size }),
      });
  
      const text = await response.text();
      const parser = new DOMParser();
      const xml = parser.parseFromString(text, 'application/xml');
  
      const quantities = Array.from(xml.getElementsByTagName('listResponse'))
        .map((item) => item.textContent);
  
      // Map cities to their corresponding quantities
      const cityQuantities = cities.reduce((acc, city, index) => {
        acc[city] = quantities[index] || 'No Info'; // 'No Info' if no quantity available
        return acc;
      }, {});
  
      // If we get a response, store the data and exit
      if (quantities.length > 0) {
        setProductResponses((prevResponses) => [
          ...prevResponses,
          { productId: `${style}-${color}-${size}`, cityQuantities },
        ]);
        return;  // Exit after successful response
      }
  
      // If no response, remove spaces from color and retry
      const colorWithoutSpaces = color.replace(/\s+/g, '');  // Remove spaces from color
      console.log('Retrying with color without spaces:', {
        style, 
        color: colorWithoutSpaces, 
        size
      });
  
      response = await fetch('https://api-shipping-28612190fdf4.herokuapp.com/inventory', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ style, color: colorWithoutSpaces, size }),
      });
  
      const retryText = await response.text();
      const retryXml = new DOMParser().parseFromString(retryText, 'application/xml');
      const retryQuantities = Array.from(retryXml.getElementsByTagName('listResponse'))
        .map((item) => item.textContent);
  
      const retryCityQuantities = cities.reduce((acc, city, index) => {
        acc[city] = retryQuantities[index] || 'No Info';
        return acc;
      }, {});
  
      // If we get a response, store the data and exit
      if (retryQuantities.length > 0) {
        setProductResponses((prevResponses) => [
          ...prevResponses,
          { productId: `${style}-${colorWithoutSpaces}-${size}`, cityQuantities: retryCityQuantities },
        ]);
        return;  // Exit after successful response
      }
  
      // If still no response, log that no data is available
      console.log('No data available for product after all retries.');
      setProductResponses((prevResponses) => [
        ...prevResponses,
        { productId: `${style}-${colorWithoutSpaces}-${size}`, cityQuantities: { error: 'No data available' } },
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      setProductResponses((prevResponses) => [
        ...prevResponses,
        { productId: `${style}-${color}-${size}`, cityQuantities: { error: 'No data available' } }
      ]);
    }
  };
  
  
  
  return (
    <div>
      <h1>Product Details</h1>
      {error && <p style={{ color: 'red' }}>Error: {error}</p>}  {/* Show error message if there's an error */}
      {loading ? (
        <p>Loading...</p> // Show loading message while data is being fetched
      ) : (
        <div className="product-grid">
          {products.map((product, index) => {
            // Generate a composite key using the product's attributes
            const compositeKey = `${product.style}-${product.color}-${product.size}-${index}`;

            // Find the response for this product
            const productResponse = productResponses.find(
              (response) => response.productId === `${product.style}-${product.color}-${product.size}`
            );

            return (
              <div key={compositeKey} className="product-card">
                <h2>Product ID: {product.id}</h2>
                <p><strong>Color:</strong> {product.color}</p>
                <p><strong>Price:</strong> ${parseFloat(product.price || 0).toFixed(2)}</p>
                <p><strong>Quantity:</strong> {product.quantity}</p>
                <p><strong>Size:</strong> {product.size}</p>
                <p><strong>Style:</strong> {product.style}</p>
                
                {/* Check if rating exists; if not, show a fallback message */}
                <p><strong>Rating:</strong> {product.rating ? product.rating : "Customer did not rate the product"}</p>
                
                <img src={product.thumImage} alt="Thumbnail" style={{ width: '100px', height: 'auto' }} />
                
                {/* Display the response data for this product */}
                {productResponse ? (
                  <div>
                    <h3>City Quantities:</h3>
                    <ul>
                      {Object.entries(productResponse.cityQuantities).map(([city, quantity]) => (
                        <li key={city}>
                          {city}: {quantity}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p>No Info available for city quantities.</p> // Show "No Info" if no response data
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProductPage;
